import { Tooltip } from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Shared explainer for what "primary license" means. Primary is the license for
 * the state where the CPA principally practices (their principal place of
 * business) — the anchor for future home-state reciprocity features, not just
 * "the license shown first". Keep every surface using this same copy.
 */
export const PRIMARY_LICENSE_EXPLAINER =
  'Your primary license is for the state where you principally practice (your principal place of business).';

/** Third-person variant for admin views of another member's licenses. */
export const PRIMARY_LICENSE_EXPLAINER_MEMBER =
  "This member's primary license: the state where they principally practice (their principal place of business).";

interface PrimaryLicenseChipProps {
  /** Use third-person copy for admin views of another member's licenses. */
  memberView?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Primary-license marker: a small home glyph beside the state name with a
 * tooltip explaining principal-place-of-business semantics (home echoes both
 * "principal place of business" and home-state reciprocity). Deliberately NOT
 * a chip — "Primary" is identity metadata, and a chip-shaped badge reads as a
 * third status competing with the Compliant / In progress / At risk standing chip.
 */
export default function PrimaryLicenseChip({ memberView = false, sx }: PrimaryLicenseChipProps) {
  return (
    <Tooltip title={memberView ? PRIMARY_LICENSE_EXPLAINER_MEMBER : PRIMARY_LICENSE_EXPLAINER} arrow>
      {/* titleAccess (not aria-label) — SvgIcon is aria-hidden by default, and
          titleAccess is what removes that and exposes the name to AT. */}
      <HomeRoundedIcon
        titleAccess="Primary license"
        sx={[{ fontSize: 18, color: 'primary.main' }, ...(Array.isArray(sx) ? sx : [sx])]}
      />
    </Tooltip>
  );
}
